import React, { useEffect, useState } from "react";
import { PALETTE } from "../design-tools/tokens";

/**
 * Brand loader — dark-teal theme, counts 0→100% over 2.8s.
 * Matches the site's v2 design system.
 * Calls onDone() once it reaches 100%.
 */
export default function Loader({ onDone, label = "PREPARING" }) {
  const [pct, setPct] = useState(0);
  const DURATION = 2800;

  useEffect(() => {
    let raf, start;
    const tick = (t) => {
      if (!start) start = t;
      const elapsed = t - start;
      const p = Math.min(100, Math.round((elapsed / DURATION) * 100));
      setPct(p);
      if (elapsed < DURATION) raf = requestAnimationFrame(tick);
      else if (onDone) onDone();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <div style={styles.root}>
      {/* Subtle radial glow */}
      <div aria-hidden="true" style={styles.glow} />

      <div style={styles.frame}>
        <div style={styles.label}>◈ &nbsp; DIGITAL BILLIONS GROUP</div>

        {/* Counter */}
        <div style={styles.count}>
          {String(pct).padStart(3, "0")}
          <span style={styles.pct}>%</span>
        </div>

        {/* Progress bar */}
        <div style={styles.barOuter}>
          <div style={{ ...styles.barInner, width: `${pct}%` }} />
          {/* Glow nub at leading edge */}
          <div style={{
            ...styles.barNub,
            left: `${pct}%`,
            opacity: pct > 0 && pct < 100 ? 1 : 0,
          }} />
        </div>

        <div style={styles.sublabel}>{label}</div>
      </div>

      {/* Bottom telemetry */}
      <div style={styles.telem}>
        SYS:LOAD &nbsp; ◌ &nbsp; DBG-HQ &nbsp; ◌ &nbsp; MMXXVI
      </div>
    </div>
  );
}

const styles = {
  root: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#0a1210",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font-mono, 'DM Mono', monospace)",
  },

  glow: {
    position: "absolute",
    top: "30%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "600px",
    height: "400px",
    background: "radial-gradient(ellipse at center, rgba(54,196,168,0.06), transparent 65%)",
    pointerEvents: "none",
  },

  frame: {
    width: "min(560px, 80vw)",
    textAlign: "left",
    position: "relative",
    zIndex: 1,
  },

  label: {
    fontFamily: "var(--font-mono, 'DM Mono', monospace)",
    fontSize: "10px",
    letterSpacing: "0.32em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    marginBottom: 36,
  },

  count: {
    fontFamily: "var(--font-display, 'Cormorant Garamond', serif)",
    fontWeight: 300,
    fontSize: "clamp(72px, 12vw, 148px)",
    lineHeight: 1,
    letterSpacing: "-0.02em",
    fontVariantNumeric: "tabular-nums",
    color: PALETTE.white,
    marginBottom: 28,
    textShadow: "0 0 40px rgba(54,196,168,0.12)",
  },

  pct: {
    fontSize: "0.38em",
    marginLeft: 10,
    color: PALETTE.teal600,
    fontStyle: "italic",
    verticalAlign: "middle",
  },

  barOuter: {
    width: "100%",
    height: "1px",
    background: "rgba(53,143,128,0.18)",
    position: "relative",
    marginBottom: 18,
    overflow: "visible",
  },

  barInner: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    background: `linear-gradient(90deg, ${PALETTE.teal800}, ${PALETTE.teal500})`,
    transition: "width 0.05s linear",
  },

  barNub: {
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: PALETTE.teal400,
    boxShadow: "0 0 8px rgba(54,196,168,0.7), 0 0 20px rgba(54,196,168,0.3)",
    transition: "left 0.05s linear, opacity 0.2s ease",
  },

  sublabel: {
    fontFamily: "var(--font-mono, 'DM Mono', monospace)",
    fontSize: "9px",
    letterSpacing: "0.32em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
  },

  telem: {
    position: "absolute",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    fontFamily: "var(--font-mono, 'DM Mono', monospace)",
    fontSize: "9px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(36,130,119,0.35)",
    whiteSpace: "nowrap",
  },
};
